import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Sparkles, ClipboardCheck, CalendarClock, BarChart3, Workflow } from 'lucide-react';

const services = [
  {
    icon: Code2,
    title: 'Custom Web & Mobile Apps',
    description:
      'Polished, production-grade websites and apps built on a modern stack — fast, responsive, and designed to convert. Your software, owned by you.',
  },
  {
    icon: Sparkles,
    title: 'AI Automation & Assistants',
    description:
      'Practical AI woven into your operations: assistants that read your live data, weekly insight digests, document parsing, and image generation that actually saves time.',
  },
  {
    icon: ClipboardCheck,
    title: 'Field-Service & Ops SaaS',
    description:
      'Estimating, scheduling, invoicing, and payments for trades and service businesses — including on-site photo measurement and customer-facing portals.',
  },
  {
    icon: CalendarClock,
    title: 'Booking & Scheduling Platforms',
    description:
      'Multi-role booking systems with calendars, dispatch, live status tracking, memberships, and automated reminders — for any business that runs on appointments.',
  },
  {
    icon: BarChart3,
    title: 'Dashboards & Analytics',
    description:
      'Turn raw numbers into decisions with interactive calculators, financial modeling, and clean dashboards that surface what matters at a glance.',
  },
  {
    icon: Workflow,
    title: 'Internal Tools & CRM',
    description:
      'Custom CRMs, pipelines, and internal tooling that fit your process exactly — with milestone billing, integrations, and automation built in.',
  },
];

const Services: React.FC = () => {
  return (
    <section id="services" className="py-20 bg-black relative overflow-hidden scroll-mt-24">
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-gold-950/20 to-black/90"
        animate={{ backgroundPosition: ['center top', 'center bottom', 'center top'] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        style={{ backgroundSize: 'auto 200%' }}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-14 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gold-400 mb-4">What We Build</h2>
          <p className="text-gray-400">
            One studio, the full stack. We take small businesses from idea to shipped software — and keep it running as you grow.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                className="group bg-gray-900/70 border border-gold-800/30 rounded-3xl p-7 shadow-xl shadow-gold-900/10 backdrop-blur-sm hover:border-gold-600/50 transition-all duration-300 liquid-glass-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ y: -8, boxShadow: '0 20px 45px -20px rgba(211, 158, 61, 0.45)' }}
              >
                <div className="w-14 h-14 rounded-2xl bg-gold-400/15 border border-gold-700/30 flex items-center justify-center mb-5 text-gold-400 group-hover:scale-110 group-hover:bg-gold-400/25 transition-all duration-300">
                  <Icon className="w-7 h-7" strokeWidth={1.75} />
                </div>
                <h3 className="text-xl font-bold text-gold-400 mb-2">{service.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
